import { Option, Schema } from "effect";

export const WORKSPACE_SHORTCUT_STORAGE_KEY = "papersflow-workspace-shortcuts";

const WORKSPACE_SHORTCUT_PREFERENCE_VERSION = 1;
const WORKSPACE_SHORTCUT_KEYS = [
  "a",
  "b",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
  "j",
  "k",
  "l",
  "m",
  "n",
  "o",
  "p",
  "q",
  "r",
  "s",
  "t",
  "u",
  "v",
  "w",
  "x",
  "y",
  "z",
] as const;

const WorkspaceShortcutKeySchema = Schema.Literals(WORKSPACE_SHORTCUT_KEYS);
const WorkspaceShortcutCommandSchema = Schema.Literals([
  "command-palette",
  "toggle-sidebar",
  "toggle-right-panel",
  "toggle-right-panel-focus",
  "toggle-runtime",
]);
const WorkspaceShortcutChordSchema = Schema.Struct({
  key: WorkspaceShortcutKeySchema,
  shiftKey: Schema.Boolean,
  altKey: Schema.Boolean,
});
const WorkspaceShortcutOverrideSchema = Schema.Struct({
  command: WorkspaceShortcutCommandSchema,
  chord: WorkspaceShortcutChordSchema,
});

export type WorkspaceShortcutChord = typeof WorkspaceShortcutChordSchema.Type;
export type WorkspaceShortcutCommand = typeof WorkspaceShortcutCommandSchema.Type;
export type WorkspaceShortcutOverride = typeof WorkspaceShortcutOverrideSchema.Type;

function sameWorkspaceShortcutChord(
  left: WorkspaceShortcutChord,
  right: WorkspaceShortcutChord,
): boolean {
  return left.key === right.key && left.shiftKey === right.shiftKey && left.altKey === right.altKey;
}

interface WorkspaceShortcutDefinition extends WorkspaceShortcutChord {
  readonly title: string;
  readonly description: string;
}

export const WORKSPACE_SHORTCUTS = {
  "command-palette": {
    title: "Command palette",
    description: "Search commands, projects, and chats.",
    key: "k",
    shiftKey: false,
    altKey: false,
  },
  "toggle-sidebar": {
    title: "Toggle sidebar",
    description: "Show or hide project navigation.",
    key: "b",
    shiftKey: true,
    altKey: false,
  },
  "toggle-right-panel": {
    title: "Toggle right panel",
    description: "Show or hide the active project tool panel when it is available.",
    key: "b",
    shiftKey: false,
    altKey: true,
  },
  "toggle-right-panel-focus": {
    title: "Maximize right panel",
    description: "Maximize or restore the docked project tool panel in a wide workspace.",
    key: "m",
    shiftKey: true,
    altKey: false,
  },
  "toggle-runtime": {
    title: "Toggle runtime drawer",
    description: "Show or hide Desktop project runtime tools when they are available.",
    key: "j",
    shiftKey: false,
    altKey: false,
  },
} as const satisfies Record<WorkspaceShortcutCommand, WorkspaceShortcutDefinition>;

export const WORKSPACE_SHORTCUT_ORDER = [
  "command-palette",
  "toggle-sidebar",
  "toggle-right-panel",
  "toggle-right-panel-focus",
  "toggle-runtime",
] as const satisfies readonly WorkspaceShortcutCommand[];

function defaultWorkspaceShortcutBinding(
  command: WorkspaceShortcutCommand,
): WorkspaceShortcutChord {
  const { key, shiftKey, altKey } = WORKSPACE_SHORTCUTS[command];
  return { key, shiftKey, altKey };
}

function workspaceShortcutBindingFromOverrides(
  overrides: readonly WorkspaceShortcutOverride[],
  command: WorkspaceShortcutCommand,
): WorkspaceShortcutChord {
  return (
    overrides.find((candidate) => candidate.command === command)?.chord ??
    defaultWorkspaceShortcutBinding(command)
  );
}

const WorkspaceShortcutPreferenceSchema = Schema.Struct({
  version: Schema.Literal(WORKSPACE_SHORTCUT_PREFERENCE_VERSION),
  overrides: Schema.Array(WorkspaceShortcutOverrideSchema).check(
    Schema.isMaxLength(WORKSPACE_SHORTCUT_ORDER.length),
    Schema.makeFilter(
      (overrides) => {
        const commands = new Set(overrides.map((override) => override.command));
        if (commands.size !== overrides.length) return "commands must be unique";

        return WORKSPACE_SHORTCUT_ORDER.every((command, index) => {
          const chord = workspaceShortcutBindingFromOverrides(overrides, command);
          return (
            WORKSPACE_SHORTCUT_ORDER.findIndex((candidate) =>
              sameWorkspaceShortcutChord(
                workspaceShortcutBindingFromOverrides(overrides, candidate),
                chord,
              ),
            ) === index
          );
        });
      },
      { expected: "unique commands with non-conflicting effective single-chord bindings" },
    ),
  ),
});

type WorkspaceShortcutPreference = typeof WorkspaceShortcutPreferenceSchema.Type;

const decodeWorkspaceShortcutPreference = Schema.decodeUnknownOption(
  WorkspaceShortcutPreferenceSchema,
  {
    errors: "all",
    onExcessProperty: "error",
    reportInput: false,
  },
);
const encodeWorkspaceShortcutPreference = Schema.encodeSync(WorkspaceShortcutPreferenceSchema);

let runtimeOverrides: readonly WorkspaceShortcutOverride[] | undefined;
let preferenceRevision = 0;
const preferenceListeners = new Set<() => void>();
let storageListenerAttached = false;

function currentPlatform(): string {
  return typeof navigator === "undefined" ? "" : navigator.platform;
}

function isApplePlatform(platform: string): boolean {
  return /Mac|iPhone|iPad/u.test(platform);
}

function browserStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function storedWorkspaceShortcutPreference(storage: Storage): unknown {
  try {
    const stored = storage.getItem(WORKSPACE_SHORTCUT_STORAGE_KEY);
    return stored === null ? undefined : JSON.parse(stored);
  } catch {
    return undefined;
  }
}

function decodedSerializedPreference(value: string | null): readonly WorkspaceShortcutOverride[] {
  if (value === null) return [];
  try {
    return decodeWorkspaceShortcutOverrides(JSON.parse(value));
  } catch {
    return [];
  }
}

function emitWorkspaceShortcutPreferenceChange(): void {
  preferenceRevision += 1;
  for (const listener of preferenceListeners) listener();
}

function handleWorkspaceShortcutStorage(event: StorageEvent): void {
  if (event.key !== WORKSPACE_SHORTCUT_STORAGE_KEY && event.key !== null) return;
  runtimeOverrides = event.key === null ? [] : decodedSerializedPreference(event.newValue);
  emitWorkspaceShortcutPreferenceChange();
}

function attachWorkspaceShortcutStorageListener(): void {
  if (
    storageListenerAttached ||
    typeof window === "undefined" ||
    typeof window.addEventListener !== "function"
  ) {
    return;
  }
  window.addEventListener("storage", handleWorkspaceShortcutStorage);
  storageListenerAttached = true;
}

export function decodeWorkspaceShortcutOverrides(
  value: unknown,
): readonly WorkspaceShortcutOverride[] {
  return Option.getOrUndefined(decodeWorkspaceShortcutPreference(value))?.overrides ?? [];
}

export function readWorkspaceShortcutOverrides(): readonly WorkspaceShortcutOverride[] {
  if (runtimeOverrides !== undefined) return runtimeOverrides;
  const storage = browserStorage();
  if (!storage) return [];
  runtimeOverrides = decodeWorkspaceShortcutOverrides(storedWorkspaceShortcutPreference(storage));
  preferenceRevision += 1;
  return runtimeOverrides;
}

function persistWorkspaceShortcutOverrides(overrides: readonly WorkspaceShortcutOverride[]): void {
  runtimeOverrides = overrides;
  const preference: WorkspaceShortcutPreference = {
    version: WORKSPACE_SHORTCUT_PREFERENCE_VERSION,
    overrides,
  };
  const encoded = encodeWorkspaceShortcutPreference(preference);
  const storage = browserStorage();
  try {
    if (storage) {
      if (overrides.length === 0) {
        storage.removeItem(WORKSPACE_SHORTCUT_STORAGE_KEY);
      } else {
        storage.setItem(WORKSPACE_SHORTCUT_STORAGE_KEY, JSON.stringify(encoded));
      }
    }
  } catch {
    // The validated runtime preference still applies for this page when storage is unavailable.
  }
  emitWorkspaceShortcutPreferenceChange();
}

export function workspaceShortcutBinding(
  command: WorkspaceShortcutCommand,
): WorkspaceShortcutChord {
  return workspaceShortcutBindingFromOverrides(readWorkspaceShortcutOverrides(), command);
}

export function hasWorkspaceShortcutOverride(command: WorkspaceShortcutCommand): boolean {
  return readWorkspaceShortcutOverrides().some((override) => override.command === command);
}

export type SetWorkspaceShortcutResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly conflict: WorkspaceShortcutCommand };

export function setWorkspaceShortcutOverride(
  command: WorkspaceShortcutCommand,
  chord: WorkspaceShortcutChord,
): SetWorkspaceShortcutResult {
  const current = readWorkspaceShortcutOverrides();
  const conflict = WORKSPACE_SHORTCUT_ORDER.find(
    (candidate) =>
      candidate !== command &&
      sameWorkspaceShortcutChord(workspaceShortcutBindingFromOverrides(current, candidate), chord),
  );
  if (conflict) return { ok: false, conflict };

  const withoutCommand = current.filter((override) => override.command !== command);
  const next = sameWorkspaceShortcutChord(WORKSPACE_SHORTCUTS[command], chord)
    ? withoutCommand
    : [...withoutCommand, { command, chord }];
  persistWorkspaceShortcutOverrides(next);
  return { ok: true };
}

export function resetWorkspaceShortcutOverride(
  command: WorkspaceShortcutCommand,
): SetWorkspaceShortcutResult {
  const current = readWorkspaceShortcutOverrides();
  const next = current.filter((override) => override.command !== command);
  const defaultChord = defaultWorkspaceShortcutBinding(command);
  const conflict = WORKSPACE_SHORTCUT_ORDER.find(
    (candidate) =>
      candidate !== command &&
      sameWorkspaceShortcutChord(
        workspaceShortcutBindingFromOverrides(next, candidate),
        defaultChord,
      ),
  );
  if (conflict) return { ok: false, conflict };

  persistWorkspaceShortcutOverrides(next);
  return { ok: true };
}

export function reloadWorkspaceShortcutOverrides(): void {
  const storage = browserStorage();
  runtimeOverrides = storage
    ? decodeWorkspaceShortcutOverrides(storedWorkspaceShortcutPreference(storage))
    : undefined;
  emitWorkspaceShortcutPreferenceChange();
}

export function workspaceShortcutPreferenceSnapshot(): number {
  readWorkspaceShortcutOverrides();
  return preferenceRevision;
}

export function subscribeWorkspaceShortcutPreferences(listener: () => void): () => void {
  preferenceListeners.add(listener);
  attachWorkspaceShortcutStorageListener();
  return () => {
    preferenceListeners.delete(listener);
  };
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    typeof HTMLElement !== "undefined" &&
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName))
  );
}

function eventMatchesKey(event: KeyboardEvent, key: WorkspaceShortcutChord["key"]): boolean {
  const layoutKey = event.key.toLowerCase();
  if (layoutKey === key) return true;
  if (/^[a-z]$/u.test(layoutKey)) return false;
  return event.code === `Key${key.toUpperCase()}`;
}

function eventShortcutKey(event: KeyboardEvent): WorkspaceShortcutChord["key"] | undefined {
  const layoutKey = event.key.toLowerCase();
  const candidate = /^[a-z]$/u.test(layoutKey)
    ? layoutKey
    : /^Key[A-Z]$/u.test(event.code)
      ? event.code.slice(3).toLowerCase()
      : undefined;
  return WORKSPACE_SHORTCUT_KEYS.find((key) => key === candidate);
}

export function workspaceShortcutChordFromKeyboardEvent(
  event: KeyboardEvent,
  platform = currentPlatform(),
): WorkspaceShortcutChord | null {
  if (event.repeat || event.isComposing || event.getModifierState("AltGraph")) return null;
  const applePlatform = isApplePlatform(platform);
  if (event.metaKey !== applePlatform || event.ctrlKey === applePlatform) return null;
  const key = eventShortcutKey(event);
  return key ? { key, shiftKey: event.shiftKey, altKey: event.altKey } : null;
}

export function resolveWorkspaceShortcut(
  event: KeyboardEvent,
  platform = currentPlatform(),
): WorkspaceShortcutCommand | null {
  if (
    event.defaultPrevented ||
    event.repeat ||
    event.isComposing ||
    event.getModifierState("AltGraph") ||
    isEditableTarget(event.target) ||
    (typeof document !== "undefined" &&
      document.querySelector('dialog[open], [aria-modal="true"]') !== null)
  ) {
    return null;
  }

  const applePlatform = isApplePlatform(platform);
  if (event.metaKey !== applePlatform || event.ctrlKey === applePlatform) return null;

  for (const command of WORKSPACE_SHORTCUT_ORDER) {
    const shortcut = workspaceShortcutBinding(command);
    if (
      event.shiftKey === shortcut.shiftKey &&
      event.altKey === shortcut.altKey &&
      eventMatchesKey(event, shortcut.key)
    ) {
      return command;
    }
  }

  return null;
}

export function workspaceShortcutKeycaps(
  command: WorkspaceShortcutCommand,
  platform = currentPlatform(),
): readonly string[] {
  const shortcut = workspaceShortcutBinding(command);
  const applePlatform = isApplePlatform(platform);
  const parts = [applePlatform ? "⌘" : "Ctrl"];
  if (shortcut.altKey) parts.push(applePlatform ? "⌥" : "Alt");
  if (shortcut.shiftKey) parts.push(applePlatform ? "⇧" : "Shift");
  parts.push(shortcut.key.toUpperCase());
  return parts;
}

export function workspaceShortcutDisplayLabel(
  command: WorkspaceShortcutCommand,
  platform = currentPlatform(),
): string {
  return workspaceShortcutKeycaps(command, platform).join(isApplePlatform(platform) ? "" : "+");
}

export function workspaceShortcutAriaKeyShortcuts(
  command: WorkspaceShortcutCommand,
  platform = currentPlatform(),
): string {
  const shortcut = workspaceShortcutBinding(command);
  const parts = [isApplePlatform(platform) ? "Meta" : "Control"];
  if (shortcut.altKey) parts.push("Alt");
  if (shortcut.shiftKey) parts.push("Shift");
  parts.push(shortcut.key.toUpperCase());
  return parts.join("+");
}
